import { useState } from "react";
import { useForm } from "react-hook-form";
import dynamic from "next/dynamic";
import {
  getModelsConsultationScheduleEmbedUrl,
  MODELS_CALLBACK_SOURCE,
  MODELS_CONSULTATION_SOURCE,
  type ModelsConsultationSource,
} from "@/lib/models-consultation";

const Modal = dynamic(() => import("@/components/molecules/modal"));

type ModelsConsultationFormValues = {
  name: string;
  surname: string;
  phone: string;
  email: string;
  message: string;
};

type ModelsConsultationFormProps = {
  source?: ModelsConsultationSource;
  heading?: string;
  description?: string;
  submitLabel?: string;
  onSubmitted?: (leadId?: string) => void;
  onClose?: () => void;
  /** When true, open the Google diary after a successful submit. */
  openDiaryOnSuccess?: boolean;
  compact?: boolean;
};

export default function ModelsConsultationForm({
  source = MODELS_CONSULTATION_SOURCE,
  heading,
  description,
  submitLabel,
  onSubmitted,
  onClose,
  openDiaryOnSuccess = true,
  compact = false,
}: ModelsConsultationFormProps) {
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [showCalendar, setShowCalendar] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const form = useForm<ModelsConsultationFormValues>({
    mode: "onTouched",
    reValidateMode: "onChange",
  });

  const isCallback = source === MODELS_CALLBACK_SOURCE;
  const resolvedHeading =
    heading ||
    (isCallback ? "Request a callback" : "Book a free consultation");
  const resolvedDescription =
    description ||
    (isCallback
      ? "Share your details and pick a time in our diary so we can call you about model treatments."
      : "Tell us what you are interested in, then choose a slot in our consultation diary.");
  const resolvedSubmitLabel =
    submitLabel ||
    (isCallback ? "Continue to diary" : "Continue to book a slot");

  const openCalendar = () => {
    document.body.style.overflow = "hidden";
    setShowCalendar(true);
  };

  const closeCalendar = () => {
    setShowCalendar(false);
    document.body.style.overflow = "auto";
  };

  const onSubmit = form.handleSubmit(async (values) => {
    setError(null);
    setSubmitting(true);

    try {
      const endpoint = isCallback
        ? "/api/models-callback"
        : "/api/models-consultation";

      const req = await fetch(endpoint, {
        method: "post",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...values,
          source,
          pagePath:
            typeof window !== "undefined"
              ? window.location.pathname
              : "/models/consultation/",
        }),
      });

      const res = await req.json();

      if (req.ok && res?.success) {
        setSubmitted(true);
        onSubmitted?.(res.leadId);

        if (typeof window !== "undefined" && window.gtag) {
          window.gtag("event", "generate_lead", {
            currency: "GBP",
            value: 0,
            lead_source: isCallback
              ? "Models Callback"
              : "Models Consultation",
          });
        }

        (window as any).fbq && (window as any).fbq("track", "Contact");

        if (openDiaryOnSuccess) {
          openCalendar();
        }
        return;
      }

      setError(
        isCallback
          ? "Unable to submit your callback request. Please try again."
          : "Unable to submit your consultation request. Please try again.",
      );
    } catch {
      setError("Something went wrong while sending your request.");
    } finally {
      setSubmitting(false);
    }
  });

  return (
    <>
      {submitted && !showCalendar ? (
        <div className={compact ? "space-y-4 py-2" : "space-y-5"}>
          <h3 className="text-lg font-semibold text-gray-900">
            Thanks - next pick a time
          </h3>
          <p className="text-sm text-gray-600">
            Your details are saved. Use the diary to book a slot so we can confirm
            your consultation.
          </p>
          <div className="flex flex-wrap gap-3">
            <button
              type="button"
              onClick={openCalendar}
              className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-semibold text-white"
            >
              Open consultation diary
            </button>
            {onClose ? (
              <button
                type="button"
                onClick={onClose}
                className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700"
              >
                Close
              </button>
            ) : null}
          </div>
        </div>
      ) : (
        <form
          onSubmit={onSubmit}
          className={compact ? "space-y-4 py-2" : "space-y-5"}
        >
          {!compact ? (
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                {resolvedHeading}
              </h2>
              <p className="mt-2 text-sm text-gray-600">{resolvedDescription}</p>
            </div>
          ) : (
            <p className="text-sm text-gray-600">{resolvedDescription}</p>
          )}

          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label
                htmlFor="models-consultation-name"
                className="mb-1 block text-sm font-medium text-gray-700"
              >
                First name
              </label>
              <input
                id="models-consultation-name"
                type="text"
                {...form.register("name", { required: true })}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none"
                placeholder="First name"
              />
              {form.formState.errors.name && (
                <p className="mt-1 text-xs text-red-600">
                  First name is required.
                </p>
              )}
            </div>

            <div>
              <label
                htmlFor="models-consultation-surname"
                className="mb-1 block text-sm font-medium text-gray-700"
              >
                Last name
              </label>
              <input
                id="models-consultation-surname"
                type="text"
                {...form.register("surname", { required: true })}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none"
                placeholder="Last name"
              />
              {form.formState.errors.surname && (
                <p className="mt-1 text-xs text-red-600">
                  Last name is required.
                </p>
              )}
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label
                htmlFor="models-consultation-phone"
                className="mb-1 block text-sm font-medium text-gray-700"
              >
                Phone
              </label>
              <input
                id="models-consultation-phone"
                type="text"
                {...form.register("phone", { required: true })}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none"
                placeholder="Phone number"
              />
              {form.formState.errors.phone && (
                <p className="mt-1 text-xs text-red-600">
                  Phone number is required.
                </p>
              )}
            </div>

            <div>
              <label
                htmlFor="models-consultation-email"
                className="mb-1 block text-sm font-medium text-gray-700"
              >
                Email
              </label>
              <input
                id="models-consultation-email"
                type="email"
                {...form.register("email", {
                  required: true,
                  pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                })}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none"
                placeholder="you@example.com"
              />
              {form.formState.errors.email && (
                <p className="mt-1 text-xs text-red-600">
                  A valid email is required.
                </p>
              )}
            </div>
          </div>

          <div>
            <label
              htmlFor="models-consultation-message"
              className="mb-1 block text-sm font-medium text-gray-700"
            >
              What are you interested in?{" "}
              <span className="font-normal text-gray-500">(optional)</span>
            </label>
            <textarea
              id="models-consultation-message"
              rows={compact ? 3 : 4}
              {...form.register("message")}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none"
              placeholder="Treatments, dates, or questions for the team."
            />
          </div>

          {error && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="w-full rounded-lg bg-brand-600 px-4 py-3 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-60"
          >
            {submitting ? "Submitting..." : resolvedSubmitLabel}
          </button>
        </form>
      )}

      {showCalendar ? (
        <Modal
          open={showCalendar}
          overflow="h-screen"
          full
          close={closeCalendar}
          xxxlarge={true}
        >
          <iframe
            title="Model consultation diary"
            src={getModelsConsultationScheduleEmbedUrl()}
            className="h-full w-full"
          />
        </Modal>
      ) : null}
    </>
  );
}
